import { BottomNavManu } from '@components/bottomNavMenu/BottomNavMenu';
import { HeaderType } from '@components/header/Header.constants';
import { HeaderButtons } from '@components/header/HeaderButton';
import { HeaderWrapper } from '@components/headerWrapper/HeaderWrapper';
import { IMenuItemExtended } from '@components/menu';
import { ToastType } from '@components/toast/ToastType.enum';
import { QueryStatus } from '@models/queryStatus';
import { QuoteRating, quoteRatingStatus } from '@models/quoteRating';
import { QuoteStatus, QuoteType, TQuoteApiResponse } from '@models/quotes';
import { CodeName } from '@models/quoteItem';
import { Box, Grid } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useOktaAuth } from '@okta/okta-react';
import { Forbidden } from '@pages/Forbidden/Forbidden';
import { defaultItems, defaultSubtitle, defaultTitle } from '@pages/Forbidden/Forbidden.constants';
import {
  BoundQuotePage,
  CanceledQuote,
  DeclinedQuote,
  LapsedQuote,
  ReferredQuote,
  RejectedQuote,
} from '@pages/PortalMessages';
import { CustomerSubmissionMessage } from '@pages/PortalMessages/CustomerUserMessages/CustomerSubmission';
import {
  QDDialogs,
  QDHeaderButtons,
  QuoteDetailPolicyAlertBanner,
  QuotePanelsContainer,
} from '@pages/QuoteDetails/components';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { drawerListItems } from '@pages/QuoteDetails/constants/drawerListItems';
import { Suspense } from 'react';
import {
  Action,
  TQuoteDetailDetailFunctionBody,
  initialActionItems,
  initialDialogState,
  readOnlyQuoteStatuses,
} from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { getRatingErrors } from '@pages/QuoteDetails/helpers/getRatingErrors';
import { useGetActionMap } from '@pages/QuoteDetails/helpers/useGetActionMap';
import { IDialogState } from '@pages/QuoteDetails/types';
import { useSaveChanges } from '@pages/QuoteDetails/helpers/useSaveChanges';
import { useCreateQuoteRating } from '@services/quoteRating/useCreateQuoteRating';
import { useGetQuoteRating } from '@services/quoteRating/useGetQuoteRating';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { useQuote, useUserViewedQuote } from '@services/quotes';
import { useCheckQuoteActiveBinding } from '@services/quotes/quoteBinding';
import { useSearchPolicyTransactions } from '@services/policy';
import { useUWTasks } from '@services/underwritingTasks';
import { useStore } from '@store/GlobalStore';
import { useLayoutStore } from '@store/LayoutStore';
import { useToastStore } from '@store/Toast';
import { useComposeTheme } from '@theme/theme';
import { Layout } from '@/layout';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIdlePrefetch } from '@utils/idlePrefetch';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { shallow } from 'zustand/shallow';
import { ProductOfferingId } from '@models/newQuote';
import { CUBlockingMessage } from '@pages/PortalMessages/CustomerUserMessages/CUBlockingMessage';
import { QuotePaymentStatus } from '@lio/rule-engine';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { UnderwriterToolkitTabs, UnderwriterToolkitContent } from '@pages/UnderwriterToolkit';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { useMessageDraftStore } from '@store/MessageDraftStore';
import { useStructuredQuote } from '@services/quoteDetails';
import { getIsFeatureFlagEnabled, FEATURE_FLAG } from '@utils/featureFlags';
import { ConfirmEmailModal } from '@components/modals/ConfirmEmailModal';
import { useUpdateQuoteItemAttributes } from '@services/quoteItem';
import { getUpdateCustomerEmailAttributes } from '@pages/BindQuote/helpers/getUpdateCustomerEmailAttributes';
import { getEngine } from '@lio/rule-engine';
import React from 'react';
import { PolicyTransactionStatus, PolicyTransactionTypes } from '@models/policyTransaction';
import { Joyride } from 'react-joyride';
import { useHasMissingRequiredBeforeBindFields } from '@hooks/useHasMissingRequiredBeforeBindFields';
import { useRequireBeforeBindWalkthrough } from '@hooks/useRequireBeforeBindWalkthrough';

export const QuoteDetailsView = ({ quoteId, readOnly }: { quoteId: string; readOnly?: boolean }) => {
  const theme = useComposeTheme('light');

  const { oktaAuth } = useOktaAuth();
  const { persistToast, clearPersistToast } = useToastStore();
  const [activeRating, setActiveRating] = useState<{
    isActive: boolean;
    id: string | undefined;
  }>({ isActive: false, id: undefined });

  const [dialogState, setDialogState] = useState<IDialogState>(initialDialogState);
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = useState(false);
  const [emailConfirmationPending, setEmailConfirmationPending] = useState(false);
  const [showHeaderMenu, openHeaderMenu] = useLayoutStore(
    (state) => [state.showHeaderMenu, state.openHeaderMenu],
    shallow,
  );
  invariant(quoteId);
  useStructuredQuote(quoteId);
  const quoteData = useQuote(quoteId);
  const quote = quoteData.quote as TQuoteApiResponse;
  const status = quoteData.status;
  const quoteStatus = quote?.status.id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sidebarOpen = searchParams.has('sidebarItem');

  const [currentUser, userAuth] = useStore((state) => [state.currentUser, state.userAuth], shallow);
  const { isBroker, isInternalUser, isCustomerUser, isLioSales, isUnderwriter, isLIOContractor } = userAuth;

  const isFullscreenToolkitEnabled = getIsFeatureFlagEnabled(FEATURE_FLAG.QUOTE_UW_TOOLKIT_FULLSCREEN, {
    isInternalUser,
  });

  const { data: searchQuoteRatingsData } = useSearchQuoteRatings({
    quoteId,
    enabled: isInternalUser || isCustomerUser,
    params: { pageLimit: 10 },
  });
  const alertBannerRef = useRef<any>(null);
  const alertBannerHeight = alertBannerRef.current ? alertBannerRef.current.clientHeight : 0;
  const searchQuoteRatings = searchQuoteRatingsData?.pages.flatMap<QuoteRating>((page) => page.items);
  const { underwritingTasks } = useUWTasks(
    { quoteId: quote?.id, includeTaskHistory: true, pageLimit: 100 },
    { isEnabled: isInternalUser },
  );

  const shouldCheckRenewalTransactions = [
    QuoteStatus.Canceled.id,
    QuoteStatus.Declined.id,
    QuoteStatus.Rejected.id,
  ].includes(quote?.status?.id);

  const { policyTransactions: conditionalRenewalTransactions } = useSearchPolicyTransactions(
    {
      policyId: quote?.policyId ?? '',
      transactionType: PolicyTransactionTypes.ConditionalRenewal,
      status: [PolicyTransactionStatus.Created, PolicyTransactionStatus.Processed],
      includeCanceled: false,
      pageLimit: 1,
      pageOffset: 0,
    },
    quote?.quoteType === QuoteType.Renewal && !!quote?.policyId,
  );

  const { policyTransactions: nonrenewalPolicyTransactions } = useSearchPolicyTransactions(
    {
      policyId: quote?.policyId ?? '',
      transactionType: PolicyTransactionTypes.DoNotRenew,
      status: [PolicyTransactionStatus.Created, PolicyTransactionStatus.Processed],
      includeCanceled: false,
      pageLimit: 1,
      pageOffset: 0,
    },
    shouldCheckRenewalTransactions && !!quote?.policyId,
  );

  const isPolicyMarkedForNonrenewal = (nonrenewalPolicyTransactions?.items?.length ?? 0) > 0;

  const { panelInstances } = useQuoteStore((state) => ({ panelInstances: state.panelInstances }), shallow);

  const walkthroughs = useStore((state) => state.walkthroughs);
  const hasSeenRequireBeforeBindWalkthrough = walkthroughs.requireBeforeBind === true;
  const hasMissingRequiredBeforeBind = useHasMissingRequiredBeforeBindFields({
    skip: hasSeenRequireBeforeBindWalkthrough,
  });
  const {
    steps: walkthroughSteps,
    run: walkthroughRun,
    startWalkthrough,
    handleJoyrideEvent,
    shouldShowWalkthrough,
  } = useRequireBeforeBindWalkthrough(hasMissingRequiredBeforeBind);

  const customer = quote?.customer;

  const lastRatingJob = searchQuoteRatings?.[0];

  const { saveChanges } = useSaveChanges(quoteId, panelInstances);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { userViewedQuoteAsync } = useUserViewedQuote();

  const [selectedLayoutTab] = useQuoteStore((s) => [s.selectedLayoutTab]);
  const renderWithFallback = (node: React.ReactNode) => (
    <Suspense fallback={<Box sx={{ p: 2, fontSize: 12 }}>Loading panel...</Box>}>{node}</Suspense>
  );

  const drawerItems = useMemo(() => {
    return drawerListItems
      .filter((it) => (it.isShown ? it.isShown(quote) : true))
      ?.map((item) => ({
        ...item,
        content: item.content ? renderWithFallback(item.content) : item.content,
        isDisabled: item.sidebarItemSearchParamValue === selectedLayoutTab,
      }));
  }, [quote, selectedLayoutTab]);

  // Opportunistically prefetch the code for less-common panels after initial paint
  useIdlePrefetch(
    useMemo(
      () => [
        () => import('./sidebarItems/notes/Notes'),
        () => import('./sidebarItems/messages/Messages'),
        () => import('./sidebarItems/tools/Tools'),
        () => import('./sidebarItems/underwriting/Underwriting'),
        () => import('./sidebarItems/subjectivities/Subjectivities'),
      ],
      [],
    ),
    { concurrency: 2 },
  );

  const isReadOnlyStatus = readOnlyQuoteStatuses.includes(quoteStatus as string);
  const isReadOnly = searchParams.has('viewReadOnly');
  const sidebarItem = searchParams.has('sidebarItem') && (searchParams.get('sidebarItem') as string | null);

  const { status: rateQuoteStatus } = useCreateQuoteRating();
  const { data: activeQuoteRatingJob } = useGetQuoteRating(quoteId, activeRating.id);

  const { isSmallScreen, isMobileScreen } = useGetScreenSize();
  const isDesktop = !isSmallScreen && !isMobileScreen;

  const clearDraftsForQuote = useMessageDraftStore((s) => s.clearDraftsForQuote);

  const [resetUnsavedChanges, attributesById, clearAttributeValues] = useQuoteStore((state) => [
    state.resetStore,
    state.attributesById,
    state.clearAttributeValues,
  ]);
  const activeRatingJob = searchQuoteRatings?.find(
    (rating) => rating.status === quoteRatingStatus.Pending || rating.status === quoteRatingStatus.InProgress,
  );

  const isSwimClubOffering = quote?.offering?.id === ProductOfferingId.SwimClub;

  const statusRead = () => {
    setActiveRating({ isActive: false, id: undefined });
  };

  useCheckQuoteActiveBinding(quote, { redirectToInProgress: true });

  useEffect(() => {
    return () => {
      if (!persistToast) {
        closeSnackbar();
      } else {
        clearPersistToast();
      }
    };
    // eslint-disable-next-line
  }, [persistToast]);

  useEffect(() => {
    resetUnsavedChanges();
    return () => {
      resetUnsavedChanges();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => {
      clearDraftsForQuote(quoteId);
    };
    // eslint-disable-next-line
  }, [quoteId]);

  useEffect(() => {
    if (activeRatingJob) {
      setActiveRating({ isActive: true, id: activeRatingJob.id });
    }
  }, [activeRatingJob, setActiveRating]);

  useEffect(() => {
    switch (activeQuoteRatingJob?.status) {
      case quoteRatingStatus.Succeeded:
        statusRead();
        enqueueSnackbar('Rating completed successfully', {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
        break;

      case quoteRatingStatus.Failed: {
        const ratingErrorList = getRatingErrors(activeQuoteRatingJob as any);
        statusRead();
        enqueueSnackbar('Quote Rating Error(s):', {
          persist: true,
          variant: 'errorList',
          errorList: ratingErrorList,
        });
        break;
      }
      case quoteRatingStatus.InProgress:
      case quoteRatingStatus.Pending:
        setActiveRating((prev) => ({ ...prev, isActive: true }));
        break;
      default:
        break;
    }
  }, [activeQuoteRatingJob, enqueueSnackbar]);

  useEffect(() => {
    if (!quote && currentUser) {
      userViewedQuoteAsync({
        quoteId,
        userId: currentUser.id,
      });
    }
  }, [quote, quoteId, currentUser, userViewedQuoteAsync]);

  // Email confirmation modal logic
  const { updateQuoteItemAttributesAsync } = useUpdateQuoteItemAttributes();
  const structuredQuote = useQuoteStore((state) => state.structuredQuote);

  // Find named insured item once and reuse
  const namedInsuredItem = React.useMemo(() => {
    return structuredQuote?.quoteItems.find((qi) => qi.quoteItemType.codeName === CodeName.NamedInsured);
  }, [structuredQuote]);

  // Evaluate rule engine for CustomerEmail isRequired rule
  const emailRequirementEvaluation = React.useMemo(() => {
    if (!structuredQuote || !namedInsuredItem?.attributeTypes.CustomerEmail) {
      return { isRequired: true, currentEmail: '' };
    }

    const currentEmail = namedInsuredItem?.attributeTypes.CustomerEmail?.attribute?.value as string;

    try {
      const ruleEngine = getEngine(structuredQuote);
      ruleEngine.setEngineInitialQuoteItem(namedInsuredItem.id);

      const isRequired = ruleEngine.execute(namedInsuredItem.attributeTypes.CustomerEmail.isRequired) as boolean;

      return { isRequired, currentEmail };
    } catch (error) {
      console.warn('Failed to evaluate CustomerEmail isRequired rule:', error);
      return { isRequired: true, currentEmail };
    }
  }, [structuredQuote, namedInsuredItem]);

  const handleConfirmEmail = async (email: string) => {
    setEmailConfirmationPending(true);
    try {
      if (!namedInsuredItem) {
        throw new Error('Named insured item not found');
      }

      // Get attribute types from the named insured item
      const request = getUpdateCustomerEmailAttributes({
        quoteId,
        namedInsuredItem,
        newEmail: email,
        customerEmailAttributeType: namedInsuredItem.attributeTypes.CustomerEmail,
      });

      await updateQuoteItemAttributesAsync(request);
      setIsEmailConfirmationModalOpen(false);
    } catch (error) {
      const message = await getApiErrorMessage(error, 'Failed to update email address');
      enqueueSnackbar(message, {
        variant: ToastType.Error,
      });
    } finally {
      setEmailConfirmationPending(false);
    }
  };

  useEffect(() => {
    // Show email confirmation modal on load if:
    // 1. External user (customer or broker)
    // 2. Renewal quote
    // 3. customerEmailConfirmedByUser is not true
    // 4. Modal hasn't been dismissed in this session
    if (
      quote &&
      (isCustomerUser || isBroker) &&
      namedInsuredItem &&
      quote.quoteType === QuoteType.Renewal &&
      !readOnly
    ) {
      const isEmailConfirmed = namedInsuredItem.attributeTypes['CustomerEmailConfirmedByUser']?.attribute?.value;
      const modalDismissedKey = `email-confirmation-modal-dismissed-${quoteId}`;
      const isDismissedInSession = sessionStorage.getItem(modalDismissedKey) === 'true';

      if (!isEmailConfirmed && !isDismissedInSession) {
        setIsEmailConfirmationModalOpen(true);
      }
    }
  }, [quote, namedInsuredItem, isCustomerUser, isBroker, quoteId, readOnly]);

  useEffect(() => {
    if (!showHeaderMenu && sidebarItem && isMobileScreen) {
      openHeaderMenu();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarItem, quote]);

  useEffect(() => {
    if (!isUnderwriter && !isLIOContractor) {
      const newParams = new URLSearchParams(searchParams);
      const hasUnderwritingLock = Boolean(quote?.hasUnderwritingLock);

      if (hasUnderwritingLock) {
        newParams.set('viewReadOnly', hasUnderwritingLock.toString());
      } else if (!isReadOnlyStatus) {
        newParams.delete('viewReadOnly');
      }
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [quote?.hasUnderwritingLock, searchParams, isUnderwriter, isLIOContractor, isReadOnlyStatus]);

  useEffect(() => {
    // do not set if blocked
    const quoteStatus = [
      QuoteStatus.Bound.id,
      QuoteStatus.Canceled.id,
      QuoteStatus.Declined.id,
      QuoteStatus.Expired.id,
      QuoteStatus.Rejected.id,
      QuoteStatus.Referred.id,
    ];

    if (!sidebarOpen && !!quote && !quoteStatus.includes(quote?.status.id) && !isMobileScreen) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('sidebarItem', 'navigator');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [quote]);

  useEffect(() => {
    if (panelInstances.length > 0 && shouldShowWalkthrough) {
      startWalkthrough();
    }
  }, [panelInstances.length, shouldShowWalkthrough, startWalkthrough]);

  // Block user-initiated scrolling (wheel, touch, keyboard) while walkthrough is active,
  // but allow Joyride's programmatic scrollIntoView to still work.
  useEffect(() => {
    if (!walkthroughRun) return;

    const prevent = (e: Event) => e.preventDefault();
    const preventScrollKeys = (e: KeyboardEvent) => {
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (scrollKeys.includes(e.key)) e.preventDefault();
    };

    document.addEventListener('wheel', prevent, { passive: false });
    document.addEventListener('touchmove', prevent, { passive: false });
    document.addEventListener('keydown', preventScrollKeys);

    return () => {
      document.removeEventListener('wheel', prevent);
      document.removeEventListener('touchmove', prevent);
      document.removeEventListener('keydown', preventScrollKeys);
    };
  }, [walkthroughRun]);

  const disableActionItems =
    rateQuoteStatus === QueryStatus.Pending ||
    searchQuoteRatings?.find((rating) => rating.status === quoteRatingStatus.Pending) !== undefined;

  const handleOpenReactivateQuoteDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.ReactivateQuote,
      },
    }));
  };

  const toggleDrawer = (newValue: DrawerList | null) => {
    const newItem = drawerListItems.find((item) => item.name === newValue);
    if (!newItem) return;

    const { sidebarItemSearchParamValue, sidebarTabSearchParamValue } = newItem;
    if (sidebarItemSearchParamValue) {
      const prevSidebarItem = searchParams.get('sidebarItem');
      const newParams = new URLSearchParams(searchParams);
      if (prevSidebarItem !== sidebarItemSearchParamValue) {
        newParams.set('sidebarItem', sidebarItemSearchParamValue);
        if (sidebarTabSearchParamValue) {
          newParams.set('sidebarTab', sidebarTabSearchParamValue);
        } else {
          newParams.delete('sidebarTab');
        }
        setSearchParams(newParams, { replace: true });
      } else {
        newParams.delete('sidebarItem');
        newParams.delete('sidebarTab');
        setSearchParams(newParams, { replace: true });
      }
    }
  };

  const onConfirmExit = () => {
    if (dialogState?.confirmDialog?.onConfirmation) {
      dialogState?.confirmDialog?.onConfirmation();
    }
    clearAttributeValues(Object.keys(attributesById));
    if (dialogState?.confirmDialog?.navigatePath && dialogState?.confirmDialog?.navigatePath !== '') {
      navigate(dialogState?.confirmDialog?.navigatePath);
    }
    setDialogState((prev) => ({ ...prev, confirmDialog: { ...prev.confirmDialog, show: false } }));
  };

  const actionMap = useGetActionMap({
    setDialogState,
    handleOpenReactivateQuoteDialog,
    onConfirmExit,
    toggleDrawer,
    isMobile: isMobileScreen,
    saveChanges,
    quote,
    quoteId,
  });

  const menuItems = useMemo(() => {
    if (!quote) return undefined;
    const actionItems: IMenuItemExtended<TQuoteDetailDetailFunctionBody>[] = initialActionItems;
    const hasConditionalRenewalTransaction =
      (conditionalRenewalTransactions?.items?.length ?? 0) > 0 &&
      !!conditionalRenewalTransactions?.items?.filter(
        (t) => t.policyConditionalRenewalTransaction?.renewalQuoteId === quote.id,
      )?.length;

    if (disableActionItems) {
      return actionItems.map((item) => ({
        ...item,
        disabled: true,
      }));
    }

    const quoteInfo: TQuoteDetailDetailFunctionBody = {
      quote,
      underwritingTasks,
      userAuth,
      hasConditionalRenewalTransaction,
      isPolicyMarkedForNonrenewal,
    };

    return actionItems.map((item) => ({
      ...item,
      hidden: item.isShown ? !item.isShown(quoteInfo) : item.hidden,
      action: item.actionName ? actionMap[item.actionName as Action] : item.action,
      disabled: item.isDisabled ? item.isDisabled(quoteInfo) : item.disabled,
    }));
    // eslint-disable-next-line
  }, [
    quote,
    disableActionItems,
    underwritingTasks,
    conditionalRenewalTransactions?.items?.length,
    nonrenewalPolicyTransactions?.items?.length,
    isPolicyMarkedForNonrenewal,
    isBroker,
    isInternalUser,
    isLioSales,
    isUnderwriter,
  ]);

  if (!isCustomerUser && !isInternalUser && !isReadOnly) {
    switch (quote?.status.id) {
      case QuoteStatus.Bound.id:
        return <BoundQuotePage quote={quote} />;
      case QuoteStatus.Canceled.id:
        return <CanceledQuote quote={quote} />;
      case QuoteStatus.Declined.id:
        return <DeclinedQuote quote={quote} />;
      case QuoteStatus.Expired.id:
        return <LapsedQuote quote={quote} />;
      case QuoteStatus.Rejected.id:
        return <RejectedQuote quote={quote} />;
      case QuoteStatus.Referred.id:
        return <ReferredQuote quote={quote} />;
      default:
    }
  }

  if (isCustomerUser && quote) {
    // swim club blocking page is 'top priority', shows over all others
    if (quote?.customerSubmittedOn && isSwimClubOffering) {
      return <CustomerSubmissionMessage quote={quote} />;
    }

    switch (quote.status.id) {
      case QuoteStatus.Bound.id:
        return <BoundQuotePage quote={quote} />;
      case QuoteStatus.Canceled.id:
      case QuoteStatus.Declined.id:
      case QuoteStatus.Expired.id:
      case QuoteStatus.Rejected.id:
      case QuoteStatus.Referred.id:
        return <CUBlockingMessage quote={quote} />; // internal component handles determining message

      default:
      // continue on
    }

    if (quote?.referredToBroker) {
      if (
        quote?.releasedToPolicyHolder &&
        [QuoteStatus.ReadyToBind.id, QuoteStatus.Underwritten.id].includes(quoteStatus)
      ) {
        navigate(`/quote/${quoteId}/review`);
        return null;
      }

      return <CUBlockingMessage quote={quote} />;
    }
  }

  if (
    quote?.paymentStatus === QuotePaymentStatus.Complete &&
    [QuoteStatus.ReadyToBind.id, QuoteStatus.Underwritten.id].includes(quoteStatus) &&
    quote?.quoteType !== QuoteType.Endorsement
  ) {
    navigate(`/quote/${quoteId}/review`);
    return null;
  }

  if (status === QueryStatus.Error || (quote?.quoteType === QuoteType.Endorsement && !isInternalUser)) {
    return (
      <Forbidden
        title={defaultTitle}
        subtitle={defaultSubtitle}
        items={defaultItems}
        primaryBtnText='Continue to your Homepage'
        primaryBtnRedirect='/'
        secondaryBtnText='Log out'
        secondaryBtnAction={async () => {
          await oktaAuth.signOut();
        }}
      />
    );
  }

  const HeaderButtonsComponent = (
    <QDHeaderButtons
      isReadOnly={isReadOnly}
      quoteId={quoteId}
      activeRating={activeRating}
      lastRatingJob={lastRatingJob || null}
      hasAnyPendingProcessingRatings={searchQuoteRatings?.some(
        (qr) => qr.status === 'InProgress' || qr.status === 'Pending',
      )}
      onSetActiveRating={setActiveRating}
      onStatusRead={statusRead}
      onSetConfirmDialog={(dialog) =>
        setDialogState((prev) => ({ ...prev, confirmDialog: { ...prev.confirmDialog, ...dialog } }))
      }
    />
  );

  const hideActionsMenu = isBroker || isCustomerUser;

  return (
    <ThemeProvider theme={theme}>
      <Joyride
        steps={walkthroughSteps}
        run={walkthroughRun}
        onEvent={handleJoyrideEvent}
        continuous
        options={{
          primaryColor: '#E5A100',
          zIndex: 10000,
          showProgress: true,
          overlayClickAction: false,
          buttons: ['skip', 'back', 'primary'],
          skipScroll: true,
          targetWaitTimeout: 10000,
        }}
        locale={{
          last: 'Got it',
          skip: 'Skip tutorial',
        }}
      />
      {isSmallScreen ? (
        <Layout
          title='Quote Details'
          config={{
            padding: 0,
            noDarkGradient: true,
          }}
          customHeader={
            <HeaderWrapper
              title='Quote Details'
              type={HeaderType.Detail}
              quote={quote}
              actionItems={undefined}
              showNavLinks={false}
              disableActionItems={true}
              buttons={[]}
              hasUnsavedChanges={Object.keys(attributesById).length > 0}
            />
          }
        >
          {/* Bottom buttons */}
          <BottomNavManu>
            <HeaderButtons
              buttons={[HeaderButtonsComponent]}
              actionItems={!hideActionsMenu ? menuItems : undefined}
              disableActionItems={disableActionItems}
            />
          </BottomNavManu>
          <QuoteDetailPolicyAlertBanner
            ref={alertBannerRef}
            customer={{
              matchState: customer?.matchState,
            }}
            quote={quote}
            isPolicyMarkedForNonrenewal={isPolicyMarkedForNonrenewal}
            isReadOnlyStatus={isReadOnlyStatus}
            isInternalUser={isInternalUser}
            isUnderwriter={isUnderwriter}
            isLIOContractor={isLIOContractor}
            onOpenReactivateQuoteDialog={handleOpenReactivateQuoteDialog}
          />
          <Box
            // this is the scroll element
            id='quote-detail-content'
            sx={{
              width: '100%',
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                overflowX: 'auto',
                '::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box sx={{ marginTop: `${alertBannerHeight + 20}px`, display: 'flex', justifyContent: 'center' }}>
                <QuotePanelsContainer
                  quote={quote}
                  quoteId={quoteId}
                  activeRating={activeRating}
                  isReadOnly={isReadOnly}
                />
              </Box>
            </Box>
          </Box>
          <QDDialogs
            quote={quote}
            quoteId={quoteId}
            onSetDialogState={setDialogState}
            dialogState={dialogState}
            onConfirmExit={onConfirmExit}
            onStatusRead={statusRead}
          />
        </Layout>
      ) : null}
      {isMobileScreen ? (
        <Layout
          title='Quote Details'
          customHeader={
            <HeaderWrapper
              title='Quote Details'
              hasUnsavedChanges={Object.keys(attributesById).length > 0}
              type={HeaderType.Detail}
              quote={quote}
            />
          }
        >
          {!showHeaderMenu && (
            <>
              <QuoteDetailPolicyAlertBanner
                ref={alertBannerRef}
                customer={{
                  matchState: customer?.matchState,
                }}
                quote={quote}
                isPolicyMarkedForNonrenewal={isPolicyMarkedForNonrenewal}
                isReadOnlyStatus={isReadOnlyStatus}
                isInternalUser={isInternalUser}
                isUnderwriter={isUnderwriter}
                isLIOContractor={isLIOContractor}
                onOpenReactivateQuoteDialog={handleOpenReactivateQuoteDialog}
              />
              <Grid>
                <QuotePanelsContainer
                  quote={quote}
                  quoteId={quoteId}
                  activeRating={activeRating}
                  isReadOnly={isReadOnly}
                />
                <QDDialogs
                  quote={quote}
                  quoteId={quoteId}
                  onSetDialogState={setDialogState}
                  dialogState={dialogState}
                  onConfirmExit={onConfirmExit}
                  onStatusRead={statusRead}
                />
                {/* Bottom buttons */}
                <BottomNavManu>
                  <HeaderButtons
                    buttons={[HeaderButtonsComponent]}
                    actionItems={!hideActionsMenu ? menuItems : undefined}
                    disableActionItems={disableActionItems}
                  />
                </BottomNavManu>
              </Grid>
            </>
          )}
        </Layout>
      ) : null}
      {isDesktop ? (
        <Layout
          title='Quote Details'
          config={{
            padding: 0,
            noDarkGradient: true,
            fullscreenMode: isFullscreenToolkitEnabled,
          }}
          customHeader={
            <>
              <HeaderWrapper
                title='Quote Details'
                type={HeaderType.Detail}
                quote={quote}
                actionItems={!hideActionsMenu ? menuItems : undefined}
                showNavLinks={false}
                disableActionItems={disableActionItems}
                buttons={[HeaderButtonsComponent]}
                hasUnsavedChanges={Object.keys(attributesById).length > 0}
              />
              {isFullscreenToolkitEnabled && <UnderwriterToolkitTabs quote={quote} />}
            </>
          }
          fullscreenMode={isFullscreenToolkitEnabled}
          sideDrawerProps={
            readOnly
              ? undefined
              : {
                  items: drawerItems,
                  toggleDrawer,
                  classNameToFindLastScrolled: 'panel-section',
                }
          }
        >
          <Box id={`quote-page-container`} sx={{ height: '100%' }}>
            <QDDialogs
              quote={quote}
              quoteId={quoteId}
              onSetDialogState={setDialogState}
              dialogState={dialogState}
              onConfirmExit={onConfirmExit}
              onStatusRead={statusRead}
            />

            {isFullscreenToolkitEnabled ? (
              <UnderwriterToolkitContent />
            ) : (
              <>
                <QuoteDetailPolicyAlertBanner
                  ref={alertBannerRef}
                  quote={quote}
                  customer={{
                    matchState: customer?.matchState,
                  }}
                  isPolicyMarkedForNonrenewal={isPolicyMarkedForNonrenewal}
                  isReadOnlyStatus={isReadOnlyStatus}
                  isInternalUser={isInternalUser}
                  isUnderwriter={isUnderwriter}
                  isLIOContractor={isLIOContractor}
                  onOpenReactivateQuoteDialog={handleOpenReactivateQuoteDialog}
                />
                <Box
                  // this is the scroll element
                  id='quote-detail-content'
                  sx={{
                    width: '100%',
                    '::-webkit-scrollbar': {
                      display: 'none',
                    },
                    transition: 'margin-left 0.2s',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      overflowX: 'auto',
                      '::-webkit-scrollbar': {
                        display: 'none',
                      },
                    }}
                  >
                    <Box sx={{ marginTop: `${alertBannerHeight + 20}px`, display: 'flex', justifyContent: 'center' }}>
                      <QuotePanelsContainer
                        quote={quote}
                        quoteId={quoteId}
                        activeRating={activeRating}
                        isReadOnly={isReadOnly || readOnly ? true : false}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Layout>
      ) : null}
      <ConfirmEmailModal
        isOpen={isEmailConfirmationModalOpen}
        currentEmail={emailRequirementEvaluation.currentEmail}
        onCloseModal={() => {
          setIsEmailConfirmationModalOpen(false);
          const modalDismissedKey = `email-confirmation-modal-dismissed-${quoteId}`;
          sessionStorage.setItem(modalDismissedKey, 'true');
        }}
        onConfirm={handleConfirmEmail}
        isLoading={emailConfirmationPending}
        isRequired={emailRequirementEvaluation.isRequired}
      />
    </ThemeProvider>
  );
};
